import { CloseoutChecklist } from './JobWorkspace';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemContent,
  IconButton,
} from '@mui/joy';
import { Medal as Award, PaperPlaneTilt as Send, DownloadSimple as Download, XCircle as AlertCircle, FileText, Image, CurrencyDollar as DollarSign, Paperclip } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import type { Job } from '../../types';
import { api } from '../../lib/api';

interface CoCReportModalProps {
  open: boolean;
  onClose: () => void;
  job: Job;
}

interface CoCRequest {
  id: number;
  job: number;
  job_number: string;
  job_address: string;
  status: 'draft' | 'sent' | 'completed' | 'follow_up_needed';
  status_display: string;
  total_contract_amount: string;
  depreciation_due: string;
  completion_date?: string;
  email_subject: string;
  email_body: string;
  recipient_email: string;
  pdf_url?: string;
  generation_status?: 'pending' | 'processing' | 'completed' | 'failed';
  generation_error?: string;
  celery_task_id?: string;
  sent_at?: string;
  next_followup_at?: string;
  created_at: string;
  is_async?: boolean;
  message?: string;
}

interface AttachmentInfo {
  type: 'photo' | 'document' | 'payment';
  name: string;
  count: number;
  items?: Array<{
    id: number;
    filename: string;
    amount?: string;
    date?: string;
  }>;
}

export const CoCReportModal: React.FC<CoCReportModalProps> = ({ open, onClose, job }) => {
  const queryClient = useQueryClient();
  const [activeRequest, setActiveRequest] = useState<CoCRequest | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    total_contract_amount: '',
    depreciation_due: '',
    completion_date: '',
    email_subject: '',
    email_body: '',
    recipient_email: '',
  });
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);

  // Fetch existing CoC requests for this job
  const { data: existingRequests = [], isLoading: requestsLoading } = useQuery({
    queryKey: ['coc-requests', job.id],
    queryFn: async () => {
      const response = await api.getAllPages<any>(`/api/coc-requests/?job_id=${job.id}`);
      if (response.status !== 200) throw new Error('Failed to fetch CoC requests');
      const data = response.data;
      return data;
    },
    enabled: open,
  });

  // Fetch CoC-tagged photos and documents count
  useEffect(() => {
    if (open) {
      fetchAttachmentInfo();
      // Pre-populate contract total from job value
      if (job.value) {
        setFormData(prev => ({
          ...prev,
          total_contract_amount: job.value?.toString() || '',
        }));
      }
    }
  }, [open]);

  const fetchAttachmentInfo = async () => {
    try {
      // Reset attachments before fetching new data
      const newAttachments: AttachmentInfo[] = [];
      
      // Fetch photos with 'coc' tag
      const photosResponse = await api.getAllPages<any>(`/api/photos/by-job/${job.id}/`);
      if (photosResponse.status === 200) {
        const photosData = photosResponse.data;
        // Handle both array and paginated responses
        const photos = photosData;
        const cocPhotos = photos.filter((photo: any) => 
          photo.tags?.some((tag: any) => tag.name === 'coc')
        );
        
        if (cocPhotos.length > 0) {
          newAttachments.push({
            type: 'photo',
            name: 'Photos tagged "coc"',
            count: cocPhotos.length,
            items: cocPhotos.map((photo: any) => ({
              id: photo.id,
              filename: photo.image_url?.split('/').pop() || `Photo ${photo.id}`
            }))
          });
        }
      }

      // Fetch documents with 'coc' tag
      const docsResponse = await api.getAllPages<any>(`/api/docs/?job=${job.id}`);
      if (docsResponse.status === 200) {
        const docsData = docsResponse.data;
        // Handle both array and paginated responses
        const docs = docsData;
        const cocDocs = docs.filter((doc: any) => 
          doc.tags?.some((tag: any) => tag.name === 'coc')
        );
        
        if (cocDocs.length > 0) {
          newAttachments.push({
            type: 'document',
            name: 'Documents tagged "coc"',
            count: cocDocs.length,
            items: cocDocs.map((doc: any) => ({
              id: doc.id,
              filename: doc.file_url?.split('/').pop() || doc.file_name || `Document ${doc.id}`
            }))
          });
        }
      }
      
      // Fetch invoice payments for this job
      const paymentsResponse = await api.getAllPages<any>(`/api/payments/?job_id=${job.id}`);
      if (paymentsResponse.status === 200) {
        const paymentsData = paymentsResponse.data;
        // Handle both array and paginated responses
        const payments = paymentsData;
        
        if (payments.length > 0) {
          newAttachments.push({
            type: 'payment',
            name: 'Invoice Payments',
            count: payments.length,
            items: payments.map((payment: any) => ({
              id: payment.id,
              filename: `Payment #${payment.id}`,
              amount: payment.amount,
              date: payment.payment_date
            }))
          });
          
        }
      }
      const workspace = await api.get<{report_balance:string}>(`/api/jobs/${job.id}/workspace/`);
      setFormData(prev => ({ ...prev, depreciation_due: Number(workspace.data.report_balance).toFixed(2) }));

      // Set all attachments at once
      setAttachments(newAttachments);
    } catch (error) {
      toast.error('Could not load the complete report preview. Please reopen the report to retry.');
    }
  };

  // Poll for generation status
  const pollGenerationStatus = async (requestId: number) => {
    try {
      const response = await api.get<any>(`/api/coc-requests/${requestId}/generation-status/`);
      if (response.status === 200) {
        const statusData = response.data;
        
        if (statusData.generation_status === 'completed') {
          // Generation complete, update the request
          setActiveRequest(prev => prev ? { ...prev, ...statusData } : null);
          setIsPolling(false);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          queryClient.invalidateQueries({ queryKey: ['coc-requests', job.id] });
          toast.success('Certificate of Completion generated successfully!');
        } else if (statusData.generation_status === 'failed') {
          // Generation failed
          setIsPolling(false);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          toast.error(`PDF generation failed: ${statusData.error || 'Unknown error'}`);
        }
        // Otherwise keep polling (pending/processing)
      }
    } catch (error) {
      toast.error('Could not load the complete report preview. Please reopen the report to retry.');
    }
  };

  // Create CoC request mutation
  const createRequestMutation = useMutation({
    mutationFn: async (data: { total_contract_amount: string; depreciation_due: string; completion_date?: string }) => {
      const response = await api.post<CoCRequest>(`/api/jobs/${job.id}/coc-request/`, data);

      if (response.status !== 201 && response.status !== 200 && response.status !== 202) {
        const error = response.data as any;
        throw new Error(error.error || 'Failed to create CoC request');
      }

      return response.data;
    },
    onSuccess: (data) => {
      setActiveRequest(data);
      setFormData({
        total_contract_amount: data.total_contract_amount,
        depreciation_due: data.depreciation_due,
        completion_date: data.completion_date || '',
        email_subject: data.email_subject,
        email_body: data.email_body,
        recipient_email: data.recipient_email,
      });
      
      // If async generation, start polling
      if (data.is_async || data.generation_status === 'pending' || data.generation_status === 'processing') {
        setIsPolling(true);
        toast(data.message || 'Generating PDF in the background. This may take a moment...', { icon: '⏳' });
        
        // Start polling every 2 seconds
        const interval = window.setInterval(() => {
          pollGenerationStatus(data.id);
        }, 2000);
        setPollingInterval(interval);
      }
      
      queryClient.invalidateQueries({ queryKey: ['coc-requests', job.id] });
    },
  });

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      if (!activeRequest) throw new Error('No active request');

      const response = await api.post<any>(`/api/coc-requests/${activeRequest.id}/send/`, {
        email_subject: formData.email_subject,
        email_body: formData.email_body,
        recipient_email: formData.recipient_email,
      });

      if (response.status !== 200 && response.status !== 201) {
        const error = response.data as any;
        throw new Error(error.error || 'Failed to send email');
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coc-requests', job.id] });
      onClose();
      toast.success('Certificate of Completion sent successfully!');
    },
  });

  const handleCreateRequest = () => {
    if (!formData.total_contract_amount || !formData.depreciation_due) {
      toast.error('Please enter both Total Contract Amount and Depreciation Due');
      return;
    }
    
    // Validate that values are greater than 0
    const contractAmount = parseFloat(formData.total_contract_amount);
    const depreciationDue = parseFloat(formData.depreciation_due);
    
    if (contractAmount <= 0) {
      toast.error('Total Contract Amount must be greater than $0');
      return;
    }
    
    if (depreciationDue < 0) {
      toast.error('Depreciation Due cannot be negative');
      return;
    }
    
    createRequestMutation.mutate({
      total_contract_amount: formData.total_contract_amount,
      depreciation_due: formData.depreciation_due,
      completion_date: formData.completion_date || undefined,
    });
  };

  const handleSendEmail = () => {
    if (!formData.recipient_email) {
      return;
    }
    sendEmailMutation.mutate();
  };

  // Default the recipient the way the backend does: a certificate is addressed
  // to the carrier, so the claim adjuster wins and the homeowner is the
  // fallback. Never overwrites what the sender has already typed.
  useEffect(() => {
    const defaultRecipient = job.adjuster_email || job.associated_contact?.email;
    if (open && !formData.recipient_email && defaultRecipient) {
      setFormData(prev => ({
        ...prev,
        recipient_email: defaultRecipient,
      }));
    }
  }, [open, job.adjuster_email, job.associated_contact]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setActiveRequest(null);
      setFormData({
        total_contract_amount: '',
        depreciation_due: '',
        completion_date: '',
        email_subject: '',
        email_body: '',
        recipient_email: '',
      });
      setAttachments([]);
      setIsPolling(false);
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [open, pollingInterval]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ maxWidth: 800 }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Award />
            Certificate of Completion Report
          </Box>
        </DialogTitle>

        <DialogContent>
          <CloseoutChecklist job={job} kind="coc" recipient={formData.recipient_email} />
          {/* Existing CoC Requests */}
          {existingRequests.length > 0 && !activeRequest && (
            <Alert color="warning" variant="soft" sx={{ mb: 3 }}>
              <Box>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  Previous CoC requests exist for this job
                </Typography>
                <List size="sm">
                  {existingRequests.map((req: CoCRequest) => (
                    <ListItem key={req.id}>
                      <ListItemContent>
                        <Typography level="body-sm">
                          {req.status_display} - Created {new Date(req.created_at).toLocaleDateString()}
                          {req.sent_at && ` - Sent ${new Date(req.sent_at).toLocaleDateString()}`}
                        </Typography>
                      </ListItemContent>
                      {req.pdf_url && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          component="a"
                          href={req.pdf_url}
                          target="_blank"
                        >
                          <Download size={16} />
                        </IconButton>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Alert>
          )}

          {!activeRequest ? (
            // Step 1: Enter financial details
            <Box>
              <Typography level="body-md" sx={{ mb: 3 }}>
                Enter the financial details to generate a Certificate of Completion for job {job.job_number}.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl required>
                  <FormLabel>Total Contract Amount</FormLabel>
                  <Input
                    type="number"
                    placeholder="Enter contract total"
                    startDecorator="$"
                    value={formData.total_contract_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, total_contract_amount: e.target.value }))}
                  />
                  <Typography level="body-xs" sx={{ mt: 0.5 }}>
                    The total value of the contract for this job
                  </Typography>
                </FormControl>

                <FormControl required>
                  <FormLabel>Depreciation Due</FormLabel>
                  <Input
                    type="number"
                    placeholder="Amount still owed"
                    startDecorator="$"
                    value={formData.depreciation_due}
                    onChange={(e) => setFormData(prev => ({ ...prev, depreciation_due: e.target.value }))}
                  />
                  <Typography level="body-xs" sx={{ mt: 0.5 }}>
                    Remaining balance after deducting payments (Contract Total - Payments Received)
                  </Typography>
                </FormControl>

                <FormControl>
                  <FormLabel>Completion Date (Optional)</FormLabel>
                  <Input
                    type="date"
                    value={formData.completion_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, completion_date: e.target.value }))}
                  />
                  <Typography level="body-xs" sx={{ mt: 0.5 }}>
                    Leave blank to exclude completion date from the report
                  </Typography>
                </FormControl>
              </Box>

              {/* Attachment Info */}
              {attachments.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography level="title-sm" sx={{ mb: 2 }}>
                    Attachments to be included:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {attachments.map((attachment, index) => (
                      <Box key={index}>
                        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 600 }}>
                          {attachment.type === 'photo' && <Image size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />}
                          {attachment.type === 'document' && <FileText size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />}
                          {attachment.type === 'payment' && <DollarSign size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />}
                          {attachment.name} ({attachment.count})
                        </Typography>
                        <Box sx={{ pl: 3 }}>
                          {attachment.items?.map((item, itemIndex) => (
                            <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Paperclip size={12} />
                              <Typography level="body-xs">
                                {item.filename}
                                {item.amount && ` - $${item.amount}`}
                                {item.date && ` (${new Date(item.date).toLocaleDateString()})`}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            // Step 2: Review and send email
            <Box>
              {isPolling ? (
                <Alert
                  color="warning"
                  variant="soft"
                  sx={{ mb: 3 }}
                  startDecorator={<CircularProgress size="sm" />}
                >
                  Generating PDF... This may take a moment for large reports.
                </Alert>
              ) : activeRequest.generation_status === 'failed' ? (
                <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                  <AlertCircle />
                  PDF generation failed: {activeRequest.generation_error || 'Unknown error'}
                </Alert>
              ) : (
                <Alert color="success" variant="soft" sx={{ mb: 3 }}>
                  Certificate generated successfully! Review the email details below before sending.
                </Alert>
              )}

              {activeRequest.pdf_url && !isPolling && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startDecorator={<Download />}
                    component="a"
                    href={activeRequest.pdf_url}
                    target="_blank"
                  >
                    Download PDF Preview
                  </Button>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl required>
                  <FormLabel>Recipient Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, recipient_email: e.target.value }))}
                    placeholder="customer@example.com"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email Subject</FormLabel>
                  <Input
                    value={formData.email_subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_subject: e.target.value }))}
                    placeholder="Certificate of Completion"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email Body</FormLabel>
                  <Textarea
                    value={formData.email_body}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_body: e.target.value }))}
                    minRows={8}
                    placeholder="Enter the email message..."
                  />
                </FormControl>
              </Box>

              {/* Attachment Info in Email Step */}
              {attachments.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography level="title-sm" sx={{ mb: 2 }}>
                    Attachments to be sent:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {attachments.map((attachment, index) => (
                      <Box key={index}>
                        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 600 }}>
                          {attachment.type === 'photo' && <Image size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />}
                          {attachment.type === 'document' && <FileText size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />}
                          {attachment.type === 'payment' && <DollarSign size={16} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />}
                          {attachment.name} ({attachment.count})
                        </Typography>
                        <Box sx={{ pl: 3 }}>
                          {attachment.items?.map((item, itemIndex) => (
                            <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Paperclip size={12} />
                              <Typography level="body-xs">
                                {item.filename}
                                {item.amount && ` - $${item.amount}`}
                                {item.date && ` (${new Date(item.date).toLocaleDateString()})`}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          {!activeRequest ? (
            <Button
              variant="solid"
              color="primary"
              onClick={handleCreateRequest}
              loading={createRequestMutation.isPending}
              disabled={!formData.total_contract_amount || !formData.depreciation_due}
            >
              Generate Certificate
            </Button>
          ) : (
            <Button
              variant="solid"
              color="primary"
              startDecorator={<Send />}
              onClick={handleSendEmail}
              loading={sendEmailMutation.isPending || isPolling}
              disabled={!formData.recipient_email || isPolling || activeRequest?.generation_status === 'failed'}
            >
              {isPolling ? 'Generating PDF...' : 'Send Email'}
            </Button>
          )}
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};